import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyAppaltiService from "../../../services/safety/SafetyAppaltiService";
import AppaltoDettaglioModal from "./AppaltoDettaglioModal";

const EMPTY = {
    id_tipo_app: "",
    ragione_sociale_impresa: "",
    partita_iva: "",
    codice_fiscale: "",
    tipo_relazione: "SUBAPPALTATORE",
    oggetto_appalto: "",
    titolo: "",
    numero_protocollo: "",
    data_inizio: "",
    data_fine: "",
    data_rilascio: "",
    data_scadenza: "",
    periodicita_mesi: "",
    note: "",
};

const RELAZIONI = [
    { value: "SUBAPPALTATORE", label: "Subappaltatore" },
    { value: "APPALTATORE", label: "Appaltatore" },
    { value: "FORNITORE", label: "Fornitore critico" },
    { value: "COMMITTENTE", label: "Committente / commessa" },
];

const APP_HELP = {
    DURC_IMPRESA: "DURC dell'impresa esterna: validità tipica 120 giorni. Distinto dal DURC della propria azienda (tab Autorizzazioni).",
    DUVRI: "Valutazione dei rischi da interferenze tra le imprese operanti sullo stesso sito/cantiere.",
    IDONEITA_TP: "Documento che attesta l'idoneità tecnico-professionale dell'impresa per l'attività appaltata.",
    PATENTE_CREDITI: "Patente a crediti per imprese che operano in cantieri soggetti al D.Lgs. 159/09.",
    POS: "Piano operativo di sicurezza redatto dall'impresa esecutrice dei lavori.",
    PSC: "Piano di sicurezza e coordinamento del titolare del servizio di coordinamento.",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function groupByCategoria(tipi) {
    const groups = {};
    tipi.forEach(t => {
        const cat = t.categoria || "Altri";
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(t);
    });
    return Object.entries(groups);
}

function statoBadge(stato, giorni) {
    const map = {
        SCADUTO:     { bg: "danger",  text: "light", label: "Scaduto" },
        IN_SCADENZA: { bg: "warning", text: "dark",  label: "In scadenza" },
        VALIDO:      { bg: "success", text: "light", label: "Valido" },
        PERMANENTE:  { bg: "light",   text: "dark",  label: "Senza scadenza" },
    };
    const m = map[stato] || map.PERMANENTE;
    return (
        <span>
            <Badge bg={m.bg} text={m.text} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof giorni === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                <span className="text-muted small ms-1">
                    ({giorni < 0 ? `${Math.abs(giorni)} gg fa` : `tra ${giorni} gg`})
                </span>
            )}
        </span>
    );
}

function relazioneLabel(v) {
    return RELAZIONI.find(r => r.value === v)?.label || v || "—";
}

export default function AppaltiPanel({ idAzienda }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [filtroImpresa, setFiltroImpresa] = useState("");
    const [dettaglioAppalto, setDettaglioAppalto] = useState(null);

    const loadTipi = useCallback(async () => {
        try {
            const resp = await SafetyAppaltiService.getTipi();
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            if (Array.isArray(data)) setTipi(data);
            else if (data?.message || data?.error) {
                setTipi([]);
                setError(data.message || data.error);
            } else setTipi([]);
        } catch (err) {
            setTipi([]);
            const m = err?.message || "";
            setError(m.includes("NetworkError") || m.includes("Failed to fetch")
                ? "API non raggiungibile. Deploy backend (SafetyAppaltiController + Service) e auth.json."
                : (m || "Impossibile caricare il catalogo appalti."));
        }
    }, []);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyAppaltiService.byAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento degli adempimenti appalti.");
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const openForm = () => {
        setForm(EMPTY);
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        if (!idAzienda) return;
        if (!form.ragione_sociale_impresa?.trim()) {
            setError("Indica la ragione sociale dell'impresa esterna.");
            return;
        }
        // Documento (tipo/titolo) facoltativo: si puo' creare l'appalto con la
        // sola ragione sociale e poi gestire personale e dotazioni.
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            fd.append("ragione_sociale_impresa", form.ragione_sociale_impresa.trim());
            if (form.id_tipo_app)           fd.append("id_tipo_app", form.id_tipo_app);
            if (form.partita_iva)           fd.append("partita_iva", form.partita_iva);
            if (form.codice_fiscale)        fd.append("codice_fiscale", form.codice_fiscale);
            if (form.tipo_relazione)        fd.append("tipo_relazione", form.tipo_relazione);
            if (form.oggetto_appalto)       fd.append("oggetto_appalto", form.oggetto_appalto);
            if (form.titolo)                fd.append("titolo", form.titolo);
            if (form.numero_protocollo)     fd.append("numero_protocollo", form.numero_protocollo);
            if (form.data_inizio)           fd.append("data_inizio", form.data_inizio);
            if (form.data_fine)             fd.append("data_fine", form.data_fine);
            if (form.data_rilascio)         fd.append("data_rilascio", form.data_rilascio);
            if (form.data_scadenza)         fd.append("data_scadenza", form.data_scadenza);
            if (form.periodicita_mesi)      fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.note)                  fd.append("note", form.note);
            if (file)                       fd.append("file", file);
            await SafetyAppaltiService.create(fd);
            setMsg("Adempimento registrato.");
            setShow(false);
            setForm(EMPTY);
            setFile(null);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (a) => {
        setError(null);
        try {
            const fn = `${(a.ragione_sociale_impresa || "appalto").toString().replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyAppaltiService.download(a.id_appalto, fn);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il file.");
        }
    };

    const onDelete = async (a) => {
        if (!a?.id_appalto) return;
        if (!window.confirm("Eliminare questo record?")) return;
        try {
            await SafetyAppaltiService.deleteAppalto(a.id_appalto);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    const tipoSel = form.id_tipo_app ? tipi.find(t => Number(t.id_tipo_app) === Number(form.id_tipo_app)) : null;
    const tipoHelp = tipoSel ? APP_HELP[tipoSel.codice] : null;

    const impreseUniche = [...new Set(items.map(i => i.ragione_sociale_impresa).filter(Boolean))].sort();
    const filtroNorm = filtroImpresa.trim().toLowerCase();
    const visibleItems = filtroNorm
        ? items.filter(i => (i.ragione_sociale_impresa || "").toLowerCase().includes(filtroNorm)
            || (i.titolo || "").toLowerCase().includes(filtroNorm)
            || (i.tipo_denominazione || "").toLowerCase().includes(filtroNorm))
        : items;

    const scaduti = items.filter(i => i.stato === "SCADUTO").length;
    const inScadenza = items.filter(i => i.stato === "IN_SCADENZA").length;

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell'azienda per gestire appalti e idoneità imprese esterne.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-briefcase me-2 text-secondary" />
                    Appalti e idoneità imprese
                </h6>
                <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi adempimento
                </Button>
            </div>

            <div className="text-muted small mb-2">
                Registra <strong>imprese esterne</strong> (subappaltatori, fornitori critici, appaltatori) e i relativi documenti:
                DURC, DUVRI, idoneità T-P, patente crediti, POS/PSC. Il DURC della <em>propria</em> azienda resta nel tab Autorizzazioni e titoli.
            </div>

            {items.length > 0 && (scaduti > 0 || inScadenza > 0) && (
                <BsAlert variant={scaduti > 0 ? "danger" : "warning"} className="py-2 small">
                    {scaduti > 0 && <span><strong>{scaduti}</strong> documenti scaduti. </span>}
                    {inScadenza > 0 && <span><strong>{inScadenza}</strong> in scadenza entro 60 gg.</span>}
                </BsAlert>
            )}

            {error && <BsAlert variant="danger" className="py-2 small">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">Nuovo adempimento / impresa esterna</div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Ragione sociale impresa esterna *</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.ragione_sociale_impresa}
                                onChange={e => setForm(f => ({ ...f, ragione_sociale_impresa: e.target.value }))}
                                list="appalti-imprese-list"
                            />
                            <datalist id="appalti-imprese-list">
                                {impreseUniche.map(n => <option key={n} value={n} />)}
                            </datalist>
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="small mb-0">P. IVA</Form.Label>
                            <Form.Control size="sm" value={form.partita_iva} onChange={e => setForm(f => ({ ...f, partita_iva: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="small mb-0">Codice fiscale</Form.Label>
                            <Form.Control size="sm" value={form.codice_fiscale} onChange={e => setForm(f => ({ ...f, codice_fiscale: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Relazione</Form.Label>
                            <Form.Select size="sm" value={form.tipo_relazione} onChange={e => setForm(f => ({ ...f, tipo_relazione: e.target.value }))}>
                                {RELAZIONI.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={8}>
                            <Form.Label className="small mb-0">Oggetto commessa / cantiere</Form.Label>
                            <Form.Control size="sm" value={form.oggetto_appalto} onChange={e => setForm(f => ({ ...f, oggetto_appalto: e.target.value }))} placeholder="Es. Servizio vigilanza cantiere XY, pulizie sede…" />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Tipo documento <span className="text-muted">(facoltativo)</span></Form.Label>
                            <Form.Select size="sm" value={form.id_tipo_app} onChange={e => setForm(f => ({ ...f, id_tipo_app: e.target.value }))}>
                                <option value="">— Nessun documento / solo impresa —</option>
                                {groupByCategoria(tipi).map(([cat, catItems]) => (
                                    <optgroup key={cat} label={cat}>
                                        {catItems.map(t => (
                                            <option key={t.id_tipo_app} value={t.id_tipo_app}>{t.denominazione}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </Form.Select>
                            {tipoHelp && <Form.Text className="text-muted d-block">{tipoHelp}</Form.Text>}
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Titolo (se altro)</Form.Label>
                            <Form.Control size="sm" value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Data inizio commessa</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_inizio} onChange={e => setForm(f => ({ ...f, data_inizio: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Data fine commessa</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_fine} onChange={e => setForm(f => ({ ...f, data_fine: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Protocollo / riferimento</Form.Label>
                            <Form.Control size="sm" value={form.numero_protocollo} onChange={e => setForm(f => ({ ...f, numero_protocollo: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Data rilascio documento</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_rilascio} onChange={e => setForm(f => ({ ...f, data_rilascio: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Scadenza</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_scadenza} onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Periodicità (mesi)</Form.Label>
                            <Form.Control type="number" size="sm" min="0" value={form.periodicita_mesi} onChange={e => setForm(f => ({ ...f, periodicita_mesi: e.target.value }))} placeholder={tipoSel?.periodicita_mesi || ""} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Allegato (PDF)</Form.Label>
                            <Form.Control type="file" size="sm" accept=".pdf,.PDF" onChange={e => setFile(e.target.files?.[0] || null)} />
                        </Col>
                    </Row>
                    <div className="mt-2 d-flex gap-2">
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : "Salva"}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                    </div>
                </div>
            )}

            {items.length > 3 && (
                <Row className="g-2 mb-2">
                    <Col xs={12} md={6}>
                        <Form.Control
                            size="sm"
                            placeholder="Filtra per impresa, documento…"
                            value={filtroImpresa}
                            onChange={e => setFiltroImpresa(e.target.value)}
                        />
                    </Col>
                    <Col xs="auto" className="text-muted small align-self-center">
                        {impreseUniche.length} imprese · {visibleItems.length} record
                    </Col>
                </Row>
            )}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
            ) : visibleItems.length === 0 ? (
                <div className="text-muted small">
                    Nessun adempimento registrato per imprese esterne. Aggiungi subappaltatori, fornitori o documenti di cantiere con le relative scadenze.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Impresa / documento</th>
                            <th>Commessa</th>
                            <th>Rilascio</th>
                            <th>Scadenza</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleItems.map(a => (
                            <tr key={a.id_appalto}>
                                <td>
                                    <div className="fw-semibold">{a.ragione_sociale_impresa}</div>
                                    <div>{a.titolo || a.tipo_denominazione || "—"}</div>
                                    <div className="text-muted small">
                                        {relazioneLabel(a.tipo_relazione)}
                                        {a.partita_iva ? ` · P.IVA ${a.partita_iva}` : ""}
                                        {a.tipo_categoria ? ` · ${a.tipo_categoria}` : ""}
                                    </div>
                                </td>
                                <td className="small">{a.oggetto_appalto || "—"}</td>
                                <td>{fmtDate(a.data_rilascio)}</td>
                                <td>{fmtDate(a.data_scadenza)}</td>
                                <td>{statoBadge(a.stato, a.giorni_alla_scadenza)}</td>
                                <td className="text-end">
                                    <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setDettaglioAppalto(a)} title="Personale e dotazione">
                                        <i className="bi bi-people" />
                                    </Button>
                                    {a.path_file && (
                                        <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(a)}>
                                            <i className="bi bi-file-earmark-pdf me-1" />PDF
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(a)}>
                                        <i className="bi bi-trash" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <AppaltoDettaglioModal
                show={!!dettaglioAppalto}
                appalto={dettaglioAppalto}
                onHide={() => setDettaglioAppalto(null)}
            />
        </div>
    );
}
